import React, { createContext, useContext, useEffect, useState } from 'react';
import englishMessages from '../generated/messages.en';

const SUPPORTED_LOCALES = ['en'];
const PSEUDO_LOCALES = import.meta.env.DEV ? ['en-XA', 'ar-XB'] : [];

const LocaleContext = createContext(null);

const interpolate = (message, values = {}) => message.replace(/\{([^}]+)\}/g, (token, key) =>
  values[key] === undefined ? token : String(values[key])
);

const pseudo = (message) => `[${message.replace(/[aeiou]/gi, (letter) => `${letter}${letter}`)}]`;

export function LocaleProvider({ children }) {
  const available = [...SUPPORTED_LOCALES, ...PSEUDO_LOCALES];
  const stored = localStorage.getItem('locale') || 'en';
  const [locale, setLocaleState] = useState(available.includes(stored) ? stored : 'en');
  const direction = locale === 'ar-XB' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = locale === 'en-XA' || locale === 'ar-XB' ? 'en' : locale;
    document.documentElement.dir = direction;
  }, [direction, locale]);

  const setLocale = (nextLocale) => {
    const accepted = available.includes(nextLocale) ? nextLocale : 'en';
    localStorage.setItem('locale', accepted);
    setLocaleState(accepted);
  };

  const t = (id, values) => {
    const source = englishMessages[id];
    if (!source) {
      if (import.meta.env.DEV) console.warn(`Missing message: ${id}`);
      return id;
    }
    const translated = locale === 'en-XA' || locale === 'ar-XB' ? pseudo(source) : source;
    return interpolate(translated, values);
  };

  return (
    <LocaleContext.Provider value={{ locale, direction, available, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside LocaleProvider');
  return context;
}
